import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { conversationService, messageService } from '@/services/api'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useCategorySuggestion } from '@/hooks/useCategorySuggestion'
import { Message, Conversation } from '@/types'
import { ActionChoice } from '@/components/chat/ActionChoice'
import { CategorySuggestion } from '@/components/chat/CategorySuggestion'
import { QuestionnaireModal } from '@/components/Questionnaire/QuestionnaireModal'
import { questionnaireService } from '@/services/questionnaireService'
import styles from './ChatPage.module.css'

export default function ChatPage() {
  const { conversationId: paramConversationId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  const { } = useAuth()

  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [aiProcessing, setAiProcessing] = useState(false)
  const [error, setError] = useState('')
  const [isCrisis, setIsCrisis] = useState(false)
  const [specialistRecommendations, setSpecialistRecommendations] = useState<any[]>([])
  const [showRecommendations, setShowRecommendations] = useState(false)
  const [requestingSpecialist, setRequestingSpecialist] = useState(false)
  const [specialistJoined, setSpecialistJoined] = useState(false)
  const [specialistName, setSpecialistName] = useState('')
  const [showActionChoice, setShowActionChoice] = useState(false)
  const [actionChoiceMessage, setActionChoiceMessage] = useState('')
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false)
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null)
  const [questionnaires, setQuestionnaires] = useState<any[]>([])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Category suggestion hook
  const { loading: suggestLoading, suggestion, error: suggestError, suggest } = useCategorySuggestion()

  // Initialize or load conversation
  useEffect(() => {
    initializeConversation()
  }, [paramConversationId])

  // Fetch available questionnaires
  useEffect(() => {
    const fetchQuestionnaires = async () => {
      try {
        const data = await questionnaireService.getQuestionnaires()
        setQuestionnaires(data)
      } catch (err) {
        console.error('Error fetching questionnaires:', err)
      }
    }

    fetchQuestionnaires()
  }, [])

  const initializeConversation = async () => {
    setLoading(true)
    try {
      let conv: Conversation

      if (paramConversationId) {
        // Load existing conversation
        conv = await conversationService.get(parseInt(paramConversationId))
        const msgs = await messageService.list(conv.id)
        // Reverse messages to show oldest first (top) and newest last (bottom)
        setMessages(Array.isArray(msgs) ? msgs.reverse() : [])
      } else {
        // Check if user already has an active conversation
        const existingConversations = await conversationService.list()
        const activeConv = Array.isArray(existingConversations)
          ? existingConversations.find((c: Conversation) => c.status === 'active' || c.status === 'transferred')
          : undefined

        if (activeConv) {
          // Redirect to existing active conversation
          navigate(`/chat/${activeConv.id}`, { replace: true })
          return
        }

        // Create new conversation only if no active one exists
        conv = await conversationService.create()
        navigate(`/chat/${conv.id}`, { replace: true })
      }

      setConversation(conv)
    } catch (err) {
      setError('Failed to load conversation')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // WebSocket integration
  const { isConnected } = useWebSocket({
    conversationId: conversation?.id || 0,
    onMessage: (message) => {
      // Check if message already exists to prevent duplicates
      setMessages((prev) => {
        const exists = prev.some(m => m.id === message.id)
        if (exists) {
          return prev // Don't add duplicate
        }
        return [...prev, message]
      })
      scrollToBottom()

      // If AI message, check for recommendations
      if (message.sender_type === 'ai') {
        setAiProcessing(false)
      }

      // If specialist message and it's the first one, show notification
      if (message.sender_type === 'specialist' && !specialistJoined && conversation?.status === 'transferred') {
        setSpecialistJoined(true)
        setSpecialistName('Specialist') // TODO: Get specialist name from message or conversation

        // Auto-hide notification after 10 seconds
        setTimeout(() => {
          setSpecialistJoined(false)
        }, 10000)
      }
    },
    onActionChoice: (message) => {
      setActionChoiceMessage(message)
      setShowActionChoice(true)
    },
  })

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleRefreshConnection = () => {
    // Force reload the page to reconnect WebSocket
    window.location.reload()
  }

  const handleStartNewConversation = async () => {
    try {
      // Close current conversation
      if (conversation) {
        await conversationService.close(conversation.id)
      }
      // Navigate to chat root - will create new conversation
      navigate('/chat')
    } catch (err) {
      console.error('Error starting new conversation:', err)
      // Navigate anyway
      navigate('/chat')
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!inputValue.trim() || !conversation) return

    const content = inputValue.trim()
    setInputValue('')
    setError('')
    setIsCrisis(false)

    try {
      // Send via REST API (creates message in DB)
      await messageService.send(conversation.id, content)
      // Don't add message here - it will come via WebSocket

      // Only request AI response if conversation is NOT transferred
      if (conversation.status !== 'transferred') {
        setAiProcessing(true)
        const aiResponse = await messageService.getAIResponse(conversation.id)

        // AI response will come via WebSocket, so we don't need to add it manually

        // Handle crisis detection
        if (aiResponse.is_crisis) {
          setIsCrisis(true)
        }

        // T039: Handle action choice trigger (at 6 messages)
        if (aiResponse.show_action_choice) {
          setActionChoiceMessage('Choose your next step: Talk to a specialist or take an assessment?')
          setShowActionChoice(true)
        }
        // Handle specialist recommendations (only if not showing action choice)
        else if (aiResponse.should_recommend_specialist && aiResponse.specialist_recommendations) {
          setSpecialistRecommendations(aiResponse.specialist_recommendations)
          setShowRecommendations(true)
        }

        setAiProcessing(false)
      }

      inputRef.current?.focus()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send message')
      setAiProcessing(false)
    }
  }

  const handleSelectSpecialist = async (specialistId: number, specialistName: string) => {
    if (!conversation) return

    setRequestingSpecialist(true)
    setError('')

    try {
      await conversationService.requestSpecialist(conversation.id, specialistId)

      // Hide recommendations
      setShowRecommendations(false)

      // Show specialist joined notification
      setSpecialistJoined(true)
      setSpecialistName(specialistName)

      // Update conversation state
      setConversation({ ...conversation, status: 'transferred' })

      // Auto-hide notification after 10 seconds
      setTimeout(() => {
        setSpecialistJoined(false)
      }, 10000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to request specialist')
    } finally {
      setRequestingSpecialist(false)
    }
  }

  const handleTalkToSpecialist = async () => {
    if (!conversation) return

    setShowActionChoice(false)
    setError('')

    try {
      // Fetch specialist recommendations
      const response = await conversationService.getSpecialistRecommendations(conversation.id)
      if (response.recommendations && response.recommendations.length > 0) {
        setSpecialistRecommendations(response.recommendations)
        setShowRecommendations(true)
      } else {
        setError('No specialist recommendations available at this time.')
      }
    } catch (err: any) {
      setError('Failed to get specialist recommendations')
      console.error('Error fetching specialist recommendations:', err)
    }
  }

  const handleTakeAssessment = async () => {
    if (!conversation) return

    setShowActionChoice(false)
    setError('')

    console.log('handleTakeAssessment called for conversation:', conversation.id)

    try {
      console.log('Calling suggest for conversation:', conversation.id)
      await suggest(conversation.id)
      console.log('suggest completed, suggestion state will update via hook')
    } catch (err: any) {
      console.error('Error in handleTakeAssessment:', err)
      setError('Failed to get category suggestion')
    }
  }

  const handleAcceptCategory = async (category: string) => {
    if (!conversation) return

    setError('')

    try {
      console.log('Looking for questionnaire with category:', category)
      console.log('Available questionnaires:', questionnaires)

      // Find questionnaire matching the category
      const questionnaire = questionnaires.find(q => q.category === category)

      if (questionnaire) {
        console.log('Found questionnaire:', questionnaire)
        setSelectedQuestionnaireId(questionnaire.id)
        setShowQuestionnaireModal(true)
      } else {
        console.error('No questionnaire found for category:', category)
        console.error('Available categories:', questionnaires.map(q => q.category))
        setError(`Questionnaire not found for category: ${category}. Available: ${questionnaires.map(q => q.category).join(', ') || 'none'}`)
      }
    } catch (err: any) {
      console.error('Error in handleAcceptCategory:', err)
      setError('Failed to process category selection')
    }
  }

  const handleOverrideCategory = () => {
    console.log('User chose to override category')
  }

  const handleDismissActionChoice = () => {
    setShowActionChoice(false)
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Loading conversation...</p>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className={styles.loadingContainer}>
        <p className={styles.loadingText}>Failed to load conversation.</p>
        <button className={styles.backButton} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className={styles.chatContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h2 className={styles.headerTitle}>Mental Health Support Chat</h2>
          <p className={styles.headerStatus}>
            {isConnected && <span className={styles.statusIndicator}></span>}
            {isConnected ? 'Connected' : 'Disconnected'}
          </p>
        </div>
        <div className={styles.headerButtons}>
          <button className={styles.refreshButton} onClick={handleRefreshConnection} title="Refresh connection">
            🔄 Refresh
          </button>
          <button className={styles.newChatButton} onClick={handleStartNewConversation} title="Start new conversation">
            ➕ New Chat
          </button>
          <button className={styles.backButton} onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Crisis Warning */}
      {isCrisis && (
        <div className={styles.crisisWarning}>
          <strong>⚠️ Crisis Support Resources</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            If you're in immediate danger, please call 988 (Suicide & Crisis Lifeline) or text HOME to 741741 (Crisis Text Line).
          </p>
        </div>
      )}

      {/* Specialist Joined Notification */}
      {specialistJoined && (
        <div className={styles.specialistNotification}>
          <div>
            <strong>✓ Specialist Connected</strong>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
              {specialistName} has been notified and will join the conversation shortly.
            </p>
          </div>
          <button className={styles.closeButton} onClick={() => setSpecialistJoined(false)}>
            ✕
          </button>
        </div>
      )}

      {/* Messages */}
      <div className={styles.messagesContainer}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <p>👋 Welcome! I'm here to listen and support you.</p>
            <p>Feel free to share what's on your mind.</p>
          </div>
        )}

        {messages.map((message) => {
          const isUser = message.sender_type === 'user'
          const isAI = message.sender_type === 'ai'
          const senderType = isUser ? 'user' : isAI ? 'ai' : 'specialist'

          return (
            <div
              key={message.id}
              className={`${styles.messageWrapper} ${styles[senderType]}`}
            >
              <div className={`${styles.messageBubble} ${styles[senderType]}`}>
                {!isUser && (
                  <div className={styles.messageSender}>
                    {isAI ? '🤖 AI Assistant' : '👨‍⚕️ Specialist'}
                  </div>
                )}
                <div className={styles.messageContent}>
                  {message.content}
                </div>
                <div className={styles.messageTimestamp}>
                  {formatTimestamp(message.created_at)}
                </div>
              </div>
            </div>
          )
        })}

        {/* Specialist Recommendations - Show inline after AI message, hide if connected with specialist */}
        {showRecommendations && specialistRecommendations.length > 0 && conversation?.status !== 'transferred' && (
          <div className={styles.recommendationsPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <strong>💡 Recommended Specialists</strong>
              <button className={styles.closeButton} onClick={() => setShowRecommendations(false)}>
                ✕
              </button>
            </div>
            <p style={{ margin: '0.5rem 0', fontSize: '0.875rem' }}>
              Based on your conversation, these specialists may be able to help:
            </p>
            <div className={styles.recommendationsList}>
              {specialistRecommendations.map((specialist) => (
                <div key={specialist.id} className={styles.specialistCard}>
                  <div className={styles.specialistInfo}>
                    <div className={styles.specialistName}>{specialist.name}</div>
                    <div className={styles.specialistDetails}>
                      {specialist.specialization} • {specialist.years_of_experience} years experience
                    </div>
                    <div className={styles.specialistExplanation}>
                      {specialist.explanation}
                    </div>
                  </div>
                  <button
                    className={styles.selectButton}
                    onClick={() => handleSelectSpecialist(specialist.id, specialist.name)}
                    disabled={requestingSpecialist}
                  >
                    {requestingSpecialist ? 'Requesting...' : 'Select'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Action Choice */}
        {showActionChoice && (
          <ActionChoice
            message={actionChoiceMessage}
            onSelectSpecialist={handleTalkToSpecialist}
            onSelectAssessment={handleTakeAssessment}
            onDismiss={handleDismissActionChoice}
          />
        )}

        {/* Category Suggestion Loading */}
        {suggestLoading && (
          <div className={styles.typingIndicator}>
            <div className={styles.typingBubble}>
              <div className={styles.messageSender}>
                🤖 Analyzing conversation...
              </div>
              <div className={styles.typingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* Category Suggestion */}
        {suggestion && !suggestLoading && (
          <CategorySuggestion
            suggestion={suggestion}
            onAccept={handleAcceptCategory}
            onOverride={handleOverrideCategory}
          />
        )}

        {/* Category Suggestion Error */}
        {suggestError && !suggestLoading && (
          <div className={styles.crisisWarning}>
            <strong>⚠️ Category Suggestion Error</strong>
            <p style={{ margin: '0.5rem 0 0 0' }}>
              {suggestError}
            </p>
          </div>
        )}

        {aiProcessing && (
          <div className={styles.typingIndicator}>
            <div className={styles.typingBubble}>
              <div className={styles.messageSender}>
                🤖 AI Assistant
              </div>
              <div className={styles.typingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className={styles.inputArea}>
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            disabled={aiProcessing}
            className={styles.messageInput}
            autoFocus
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || aiProcessing}
            className={styles.sendButton}
          >
            {aiProcessing ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>

      {/* Questionnaire Modal (T058-T062) */}
      {showQuestionnaireModal && selectedQuestionnaireId && conversation && (
        <QuestionnaireModal
          isOpen={showQuestionnaireModal}
          onClose={() => {
            setShowQuestionnaireModal(false)
            setSelectedQuestionnaireId(null)
          }}
          questionnaireId={selectedQuestionnaireId}
          conversationId={conversation.id}
        />
      )}
    </div>
  )
}
